import { Scales } from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import type { DDQuestionMatch } from "../types";

interface DDQuestionMatchesProps {
  matches: DDQuestionMatch[];
  selectedLabel: { label: string; labelId: number } | undefined;
  linkingLabelToQuestion: { questionId: number; labelId: number; labelName: string } | null;
  onLinkLabel: (questionId: number, labelId: number, labelName: string, questionText: string) => void;
}

export function DDQuestionMatches({
  matches,
  selectedLabel,
  linkingLabelToQuestion,
  onLinkLabel,
}: DDQuestionMatchesProps) {
  if (!matches.length) return null;

  return (
    <div className="mt-2 pt-2 border-t border-neutral-200">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Scales className="h-3.5 w-3.5 text-blue-600" weight="duotone" />
        <span className="text-[10px] text-blue-600 font-medium">
          Dit document kan deze DD vragen beantwoorden:
        </span>
      </div>
      <div className="space-y-1.5">
        {matches.slice(0, 3).map((match) => {
          const isLinking = linkingLabelToQuestion?.questionId === match.questionId;

          return (
            <div
              key={match.questionId}
              className="flex items-start gap-2 p-2 bg-blue-50 rounded-md border border-blue-100"
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs text-neutral-700 font-medium truncate">
                  {match.questionText}
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-[10px] text-neutral-500 truncate cursor-help">
                        {match.matchReason}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p className="text-xs">{match.matchReason}</p>
                      <p className="text-[10px] text-neutral-400 mt-1">
                        Huidige gekoppelde label: {match.linkedLabelName}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Badge variant="outline" className="text-[9px] h-4 px-1">
                    {Math.round(match.confidence * 100)}% match
                  </Badge>
                  <span className="text-[9px] text-neutral-400">
                    via: {match.linkedLabelName}
                  </span>
                </div>
              </div>
              {selectedLabel && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[10px] border-blue-200 text-blue-700 hover:bg-blue-100 flex-shrink-0"
                        disabled={isLinking}
                        onClick={() => onLinkLabel(
                          match.questionId,
                          selectedLabel.labelId,
                          selectedLabel.label,
                          match.questionText
                        )}
                      >
                        {isLinking ? (
                          <Spinner className="h-3 w-3" />
                        ) : (
                          <>Koppel</>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p className="text-xs">
                        Koppel label "{selectedLabel.label}" aan deze vraag.
                        Dit zorgt ervoor dat bestanden met dit label ook worden
                        geëvalueerd voor deze DD vraag.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        })}
        {matches.length > 3 && (
          <p className="text-[10px] text-blue-500 italic">
            + {matches.length - 3} meer vragen...
          </p>
        )}
      </div>
    </div>
  );
}
